import { useState } from 'react';

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What kind of testing do you perform?',
      answer: 'We use the IVA2 Continuous Performance Test. The Integrated Visual and Auditory Continuous Performance Test is a computerized neuropsychological test designed to measure attention and response control. It is often used as one component of an ADHD evaluation but should not be used alone to diagnose ADHD.'
    },
    {
      question: 'How accurate is online ADHD testing?',
      answer: 'Our online ADHD assessments are highly accurate. Published studies shows that the IVA-2 has a sensitivity of 80-90% and a specificity of 70-90%. This means that it can correctly identify ADHD 4 out of 5 times but, like any test, it can still produce some false positive and negative results.'
    },
    {
      question: 'What will I need for testing?',
      answer: 'Testing requires a quiet space, free of distractions. You will need a computer with webcam, audio and a wired mouse.'
    },
    {
      question: 'How long does the assessment take?',
      answer: 'The initial questionnaire takes 20-30 minutes to complete at your own pace. After submission, we will schedule the testing. The test itself is approximately 30-45 minutes. You\'ll receive your complete diagnostic report within 48 hours of completing all assessment components.'
    },
    {
      question: 'Will this test give me an ADHD diagnosis?',
      answer: 'ADHD is a clinical diagnosis. This means it requires an evaluation by a licensed practitioner. IVA-2 testing provides a tool to your provider in helping make or rule out the diagnosis.'
    },
    {
      question: 'Can I get a prescription after diagnosis?',
      answer: 'Your testing will need to be reviewed by your primary care physician or psychiatrist who may decide to prescribe medication. If you do not have a psychiatric provider we can help you find one. AltaLumé Testing does not provide medication.'
    },
    {
      question: 'Will my insurance cover the assessment?',
      answer: 'At this time we do not accept insurance for testing.'
    },
    {
      question: 'Do you test children and adolescents?',
      answer: 'Currently, our assessments are designed for teens and adults ages 14 and older.'
    },
    {
      question: 'Is my information kept confidential?',
      answer: 'Absolutely. We are fully HIPAA-compliant and use bank-level encryption to protect your personal health information. Your data is stored securely and never shared without your explicit consent. All clinicians are bound by strict confidentiality agreements and professional ethics standards.'
    }
  ];

  const leftColumn = faqs.slice(0, 4);
  const rightColumn = faqs.slice(4);

  return (
    <section id="faqs" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-violet-600 font-semibold text-sm uppercase tracking-wide">Common Questions</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Everything You Need To Know
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          <div className="space-y-4">
            {leftColumn.map((faq, index) => (
              <div 
                key={index}
                className="border border-stone-200 rounded-xl overflow-hidden hover:border-violet-300 transition-colors"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-stone-50 transition-colors"
                >
                  <span className="font-bold text-gray-900 pr-4">{faq.question}</span>
                  <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-2xl text-violet-600 flex-shrink-0`}></i>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 bg-stone-50">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {rightColumn.map((faq, index) => {
              const actualIndex = index + 4;
              return (
                <div 
                  key={actualIndex}
                  className="border border-stone-200 rounded-xl overflow-hidden hover:border-violet-300 transition-colors"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === actualIndex ? null : actualIndex)}
                    className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-stone-50 transition-colors"
                  >
                    <span className="font-bold text-gray-900 pr-4">{faq.question}</span>
                    <i className={`ri-${openIndex === actualIndex ? 'subtract' : 'add'}-line text-2xl text-violet-600 flex-shrink-0`}></i>
                  </button>
                  {openIndex === actualIndex && (
                    <div className="px-6 pb-6 bg-stone-50">
                      <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center">
          <p className="text-base text-gray-700 mb-4">Still have questions?</p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-violet-600 hover:text-violet-700 font-semibold text-base cursor-pointer whitespace-nowrap"
          >
            Contact Our Team →
          </button>
        </div>
      </div>
    </section>
  );
}
